import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async';
import React, { Component } from "react"
import {
    Container,
    Button,
    Col,
    Row,
    Spinner,
} from "reactstrap"
import { Link } from "react-router-dom"
import { ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withRouter } from "../../components/Common/withRouter"
import PlanInfoForms from "./PlanInfoForms";

// Utilities
import {
    createNotificationHelpers,
    getMerchantInfo,
    getSubscriptionInfo,
    calculateTrialDays,
    extendTrialDays,
    updateUsageCharges,
    cancelSubscription,
    updateStoreStatus,
} from "../utils";

class PlanInfo extends Component {
    constructor(props) {
        super(props)
        this.state = {
            store_domain: "",
            loading: false,
            store_details: {},
            store_status: "",
            newTrialDays: 1,
            isFreePlan: false,
            is_active: true,
            isExceedTrialDays: false,
            is_cancel_sub: false,
            action: 0,
        }

        // Initialize notification helpers
        const notifications = createNotificationHelpers();
        this.notify_info = notifications.notify_info;
        this.notify_error = notifications.notify_error;
        this.notify_success = notifications.notify_success;
    }

    /**
     * Loads plan information from API
     */
    loadData = async () => {
        this.setState({ loading: true });

        try {
            const location = this.props.location || {};
            const stateData = location.state || {};
            const legacyData = location.data ? location : {};
            const data = stateData.data || legacyData.data || null;

            let store_domain, store_details, store_status;

            if (data && data.store_domain) {
                ({ store_domain, store_details, store_status } = data);
            } else {
                const params = new URLSearchParams(location.search || '');
                const storeFromQuery = params.get('store');
                if (!storeFromQuery) {
                    this.setState({ loading: false });
                    return;
                }

                const merchantData = await getMerchantInfo(storeFromQuery, this.notify_error);
                if (!merchantData) {
                    this.setState({ loading: false });
                    return;
                }

                store_domain = storeFromQuery;
                store_details = merchantData;
                store_status = 'active';
            }

            const safeDetails = store_details || {};
            const isDisabled = !(safeDetails?.is_active ?? true);

            // Get subscription info
            const subscriptionData = await getSubscriptionInfo(
                store_domain,
                safeDetails?.subscription_id || '',
                this.notify_error
            );

            if (!subscriptionData) {
                this.setState({
                    store_domain,
                    store_status,
                    store_details: safeDetails,
                    isFreePlan: safeDetails?.is_free_subscription || false,
                    is_active: isDisabled,
                    loading: false,
                });
                return;
            }

            const { trialDays, isExceedTrialDays } = calculateTrialDays(subscriptionData);

            const currentStoreDetails = this.state.is_cancel_sub && this.state.store_details
                ? this.state.store_details
                : safeDetails;

            this.setState({
                store_domain,
                store_status,
                store_details: {
                    ...currentStoreDetails,
                    subscription_info: { ...subscriptionData, trialDays },
                },
                isFreePlan: safeDetails?.is_free_subscription || false,
                is_active: isDisabled,
                loading: false,
                isExceedTrialDays,
            });
        } catch (error) {
            this.setState({ loading: false });
        }
    }

    async componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.action !== prevState.action && this.state.action !== null) {
            this.loadData();
        }
    }

    handleInputChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
    }

    /**
     * Updates plan information (trial days or usage charges)
     */
    updatePlanInfo = async (param) => {
        this.setState({ loading: true });

        try {
            const {
                additionalOrdersPrice,
                newTrialDays,
                store_domain,
                store_details
            } = this.state;

            if (param === "trialDays") {
                if (newTrialDays <= 0) {
                    this.notify_error("Trial days must be greater than 0");
                    this.setState({ loading: false });
                    return;
                }

                const success = await extendTrialDays(
                    store_domain,
                    newTrialDays,
                    store_details.subscription_id,
                    this.notify_error
                );

                if (success) {
                    this.setState(prevState => ({
                        action: prevState.action + 1,
                        store_details: {
                            ...prevState.store_details,
                            subscription_info: {
                                ...prevState.store_details.subscription_info,
                                trialDays: parseInt(newTrialDays) + parseInt(prevState.store_details.subscription_info?.trialDays || 0),
                            }
                        },
                        loading: false
                    }));
                    this.notify_success("Trial days have been updated.");
                } else {
                    this.setState({ loading: false });
                }
                return;
            }

            if (param === "additionalOrdersPrice") {
                const success = await updateUsageCharges(
                    store_domain,
                    additionalOrdersPrice,
                    this.notify_error
                );

                if (success) {
                    this.setState(prevState => ({
                        store_details: {
                            ...prevState.store_details,
                            plan_info: {
                                ...prevState.store_details?.plan_info,
                                additionalOrdersPrice,
                            }
                        },
                        loading: false
                    }));
                    this.notify_success("Plan info updated successfully");
                } else {
                    this.setState({ loading: false });
                }
            }
        } catch (error) {
            this.setState({ loading: false });
        }
    }
    /**
     * Handles enabling/disabling Veeper
     */
    handleVeeperDisable = async () => {
        const answer = window.confirm("Are you sure you want to disable veeper?");
        if (!answer) {
            return;
        }

        const { store_domain, is_active } = this.state;
        const message = is_active ? "Veeper disabled successfully" : "Veeper enabled successfully";

        const success = await updateStoreStatus(store_domain, undefined, !is_active, this.notify_error);

        if (success) {
            this.notify_success(message);
            // Redirect using React Router if available, otherwise use window.location
            setTimeout(() => {
                if (is_active) {
                    window.location.href = "/uninstalled-stores";
                } else {
                    window.location.href = "/stores";
                }
            }, 1000);
        }
    }

    /**
     * Handles switching to/from free plan
     */
    handleChangeCheckbox = async () => {
        const answer = window.confirm("Are you sure you want to change?");
        if (!answer) {
            return;
        }

        const { store_domain, isFreePlan } = this.state;
        const newIsFreePlan = !isFreePlan;

        const success = await updateStoreStatus(store_domain, newIsFreePlan, undefined, this.notify_error);

        if (success) {
            this.setState({ isFreePlan: newIsFreePlan });
            this.notify_success("Plan info updated successfully");
        }
    }

    /**
     * Handles canceling the subscription plan
     */
    handleCancelPlan = async () => {
        const answer = window.confirm("Are you sure you want to cancel the plan?");
        if (!answer) {
            return;
        }

        this.setState({ loading: true });

        try {
            const { store_domain, store_details } = this.state;
            const result = await cancelSubscription(
                store_domain,
                store_details.subscription_id,
                this.notify_error
            );

            if (result) {
                this.setState(prevState => ({
                    store_details: {
                        ...prevState.store_details,
                        plan_info: {},
                        plan_status: 'CANCELED'
                    },
                    action: prevState.action + 1,
                    is_cancel_sub: true,
                    loading: false
                }));
                this.notify_success(result?.message || "Subscription cancelled successfully");
            } else {
                this.setState({ loading: false });
            }
        } catch (error) {
            this.setState({ loading: false });
        }
    }

    render() {
        const {
            isFreePlan,
            is_active,
            loading,
            store_details,
            store_status,
            additionalOrdersPrice = store_details?.plan_info?.additionalOrdersPrice || '',
            isExceedTrialDays,
            newTrialDays = 1
        } = this.state;

        return (
            <React.Fragment>
                <div className="page-content">
                    <Helmet>
                        <title>Veeper | Subscription Plan Info </title>
                    </Helmet>
                    <Container fluid>
                        <Breadcrumbs
                            title={("Subscription Plan Info")}
                            breadcrumbItem={("Subscription Information")}
                        />
                        <ToastContainer />
                        <Row>
                            <Col md={12}>
                                <Link to={{ pathname: "/merchant-details", search: this.state.store_domain ? `?store=${encodeURIComponent(this.state.store_domain)}` : undefined, state: { data: (store_details && Object.keys(store_details).length > 0) ? store_details : { store_domain: this.state.store_domain }, storeStatus: store_status } }} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Button className="btn btn-dark">
                                        <i className="fa fa-arrow-left"></i> Store Details
                                    </Button>
                                </Link>
                            </Col>
                        </Row>

                        {loading ? (
                            <Row className="mt-4">
                                <Col md={12} className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                    <div className="text-center">
                                        <Spinner color="primary" size="lg" className="mb-3" />
                                        <p style={{ color: '#74788d', fontSize: '16px' }}>Loading plan information...</p>
                                    </div>
                                </Col>
                            </Row>
                        ) : (
                            <div className="mt-2">
                                <PlanInfoForms
                                    additionalOrdersPrice={additionalOrdersPrice}
                                    newTrialDays={newTrialDays}
                                    isExceedTrialDays={isExceedTrialDays}
                                    isFreePlan={isFreePlan}
                                    is_active={is_active}
                                    onInputChange={this.handleInputChange}
                                    onUpdatePlanInfo={this.updatePlanInfo}
                                    onToggleFreePlan={this.handleChangeCheckbox}
                                    onToggleVeeperDisable={this.handleVeeperDisable}
                                    onCancelPlan={this.handleCancelPlan}
                                    store_details={store_details}
                                    loading={loading}
                                />
                            </div>
                        )}

                    </Container>
                </div>
            </React.Fragment >
        )
    }
}

PlanInfo.propTypes = {
    t: PropTypes.any
}
export default withRouter(PlanInfo)
